/**
 * Quiz, language and timing helpers for the Snakes & Ladders stage.
 * Board geometry lives in ../../functions/boardLayout, which derives everything
 * from the theme's configurable square count.
 */

import { ensureThemeJsonArray } from "../../functions/themeAssets";

export function normalizeLangKey(value) {
  const v = String(value || "english").trim().toLowerCase();
  if (v === "hindi" || v === "marathi" || v === "english") return v;
  return "english";
}

export function getThemeLangPack(themeData, lang) {
  const key = normalizeLangKey(lang);
  const pack = themeData?.[key];
  if (pack && typeof pack === "object") return pack;
  if (themeData?.english && typeof themeData.english === "object") return themeData.english;
  return {};
}

export function normalizeQuizCategory(category) {
  const c = String(category || "").trim().toLowerCase();
  if (c === "snake" || c === "snakes") return "snake";
  if (c === "ladder" || c === "ladders") return "ladder";
  return "";
}

export function normalizeQuizQuestion(q) {
  if (!q || typeof q !== "object") return null;
  const question_name = String(q.question_name ?? q.question ?? "").trim();
  if (!question_name) return null;

  const optionKeys = [
    "option_one",
    "option_two",
    "option_three",
    "option_four",
    "option_five",
    "option_six",
  ];
  const options = Object.fromEntries(
    optionKeys.map((k) => [k, String(q[k] ?? "").trim()])
  );

  let correct_answer = String(q.correct_answer ?? "").trim();
  if (!correct_answer) {
    const aidRaw = String(q.answer_id ?? "").trim();
    const num = parseInt(aidRaw, 10);
    if (Number.isFinite(num) && num >= 1 && num <= optionKeys.length) {
      correct_answer = options[optionKeys[num - 1]] || "";
    }
    if (!correct_answer) {
      const aid = aidRaw.toLowerCase();
      const letters = ["a", "b", "c", "d", "e", "f"];
      const idx = letters.indexOf(aid);
      if (idx >= 0 && options[optionKeys[idx]]) {
        correct_answer = options[optionKeys[idx]];
      } else if (aidRaw) {
        correct_answer = aidRaw;
      }
    }
  }

  return {
    id: q.id ?? q.answer_id ?? "",
    question_name,
    ...options,
    information: String(q.information ?? "").trim(),
    category: String(q.category ?? "").trim(),
    correct_answer,
    answer_id: q.answer_id ?? "",
    showOption: q.showOption,
    language: q.language,
  };
}

export function isQuizQuestionPlayable(q) {
  const norm = normalizeQuizQuestion(q);
  if (!norm) return false;
  return questionOptions(norm).length >= 2;
}

/** Match selected option to correct_answer (text or letter A–F). */
export function answersMatch(picked, question) {
  const selection = String(picked ?? "").trim().toLowerCase();
  const correct = String(question?.correct_answer ?? "").trim().toLowerCase();
  if (!selection || !correct) return false;
  if (selection === correct) return true;
  const opts = questionOptions(question);
  const letters = ["a", "b", "c", "d", "e", "f"];
  const letterIdx = letters.indexOf(correct);
  if (letterIdx >= 0 && opts[letterIdx]) {
    return selection === String(opts[letterIdx]).trim().toLowerCase();
  }
  const num = parseInt(correct, 10);
  if (Number.isFinite(num) && num >= 1 && num <= opts.length) {
    return selection === String(opts[num - 1]).trim().toLowerCase();
  }
  const pickedIdx = opts.findIndex(
    (opt) => String(opt).trim().toLowerCase() === selection
  );
  if (pickedIdx >= 0 && letters[pickedIdx] === correct) return true;
  return false;
}

/** Human-readable correct answer for feedback popups. */
export function resolveCorrectAnswerDisplay(question) {
  const correct = String(question?.correct_answer ?? "").trim();
  const opts = questionOptions(question);
  const letters = ["a", "b", "c", "d", "e", "f"];
  const idx = letters.indexOf(correct.toLowerCase());
  if (idx >= 0 && opts[idx]) return opts[idx];
  const num = parseInt(correct, 10);
  if (Number.isFinite(num) && num >= 1 && num <= opts.length) {
    return opts[num - 1];
  }
  if (!correct && question?.answer_id) {
    const aidNum = parseInt(String(question.answer_id), 10);
    if (Number.isFinite(aidNum) && aidNum >= 1 && aidNum <= opts.length) {
      return opts[aidNum - 1];
    }
  }
  return correct;
}

/** Theme rows may hold the quiz flat, nested under the language pack, or as `questions`. */
function readQuizList(themeData, lang) {
  const direct = ensureThemeJsonArray(themeData?.quiz);
  if (direct.length) return direct;
  const nested = ensureThemeJsonArray(themeData?.[normalizeLangKey(lang)]?.quiz);
  if (nested.length) return nested;
  return ensureThemeJsonArray(themeData?.questions);
}

/** Load Snake / Ladder pools from themeData.quiz (same fields as old questions table). */
export function getQuizByCategory(themeData, lang) {
  const list = readQuizList(themeData, lang);
  const want = normalizeLangKey(lang);
  const byLang = list.filter((q) => {
    const qLang = normalizeLangKey(q?.language || "english");
    return qLang === want;
  });
  const source = byLang.length ? byLang : list;
  const snakes = [];
  const ladders = [];
  source.forEach((raw) => {
    if (!isQuizQuestionPlayable(raw)) return;
    const q = normalizeQuizQuestion(raw);
    const cat = normalizeQuizCategory(q.category);
    if (cat === "snake") snakes.push(q);
    else if (cat === "ladder") ladders.push(q);
  });
  return { snakes, ladders };
}

export function questionOptions(q) {
  const keys = [
    "option_one",
    "option_two",
    "option_three",
    "option_four",
    "option_five",
    "option_six",
  ];
  const filled = keys.map((k) => String(q?.[k] ?? "").trim()).filter(Boolean);
  const raw = parseInt(String(q?.showOption ?? "").trim(), 10);
  const count =
    Number.isFinite(raw) && raw >= 2 ? Math.min(6, raw) : filled.length;
  return filled.slice(0, Math.max(2, count || filled.length));
}

export function shuffleCopy(arr) {
  const next = arr.slice();
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const t = next[i];
    next[i] = next[j];
    next[j] = t;
  }
  return next;
}

export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
